import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) return toast.error('Please complete the human-verification check');
    setLoading(true);
    try {
      const u = await login(email, password, turnstileToken);
      toast.success(`Welcome back, ${u.full_name || u.email}`);
      const to = new URLSearchParams(loc.search).get('to') || (u.role === 'admin' ? '/admin' : '/dashboard');
      nav(to);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 bg-neutral-950 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1554629942-0c17bf68b884?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxOZXclMjBaZWFsYW5kJTIwaGlnaHdheSUyMHN1bnNldHxlbnwwfHx8fDE3ODMyMzUxNzN8MA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="relative m-16 self-end text-white max-w-md">
          <img src="/brand/logo-icon@2x.png" className="w-12 h-12 rounded-xl mb-6" alt="PTT" />
          <div className="text-xs tracking-[0.2em] uppercase text-teal-400 mb-2">PTT NZ Alerts</div>
          <h2 className="font-display font-black text-4xl leading-tight">Ops-grade highway alerting for Aotearoa.</h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="auth-logo">
            <img src="/brand/logo-icon@2x.png" alt="PTT" className="w-10 h-10 rounded-lg" />
            <span className="font-display font-black text-xl">PTT NZ Alerts</span>
          </Link>
          <h1 className="font-display font-black text-3xl mb-2">Welcome back</h1>
          <p className="text-zinc-600 mb-8">Log in to manage your highway alerts.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Password</label>
                <Link to="/forgot-password" className="text-xs font-semibold text-orange-600 hover:underline" data-testid="login-forgot">Forgot?</Link>
              </div>
              <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <TurnstileWidget onVerify={setTurnstileToken} className="pt-1" />
            <button type="submit" className="btn btn-primary w-full" disabled={loading || !turnstileToken} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log in'}
            </button>
          </form>

          <div className="mt-8 text-sm text-zinc-600 text-center">
            Don't have an account? <Link to="/signup" className="text-orange-600 font-semibold hover:underline" data-testid="login-goto-signup">Sign up free</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
